<template>
  <!-- Fondo oscuro al abrir en móvil (clic para cerrar) -->
  <div v-if="sidebarAbierta" class="sidebar-backdrop d-lg-none" @click="cerrarBarra"></div>

  <nav :class="[
    'sidebar position-fixed d-flex flex-column p-3 shadow-sm',
    sidebarAbierta ? 'sb-open' : 'sb-closed'
  ]">
    <!-- ENCABEZADO -->
    <div class="sidebar-header d-flex justify-content-between align-items-center mb-4">
      <router-link to="/dashboard" class="brand-title">
        <i class="bi bi-grid-1x2-fill me-2"></i>
        Panel
      </router-link>

      <button type="button" class="btn-close-custom" aria-label="Ocultar menú" @click="cerrarBarra">
        <i class="bi bi-chevron-left"></i>
      </button>
    </div>

    <!-- MENÚ FILTRADO POR PERMISOS (grupos siempre abiertos) -->
    <ul class="menu list-unstyled">
      <li v-for="item in menuFiltrado" :key="item.name" class="menu-group">
        <!-- Encabezado del grupo: solo etiqueta de sección, NO colapsa -->
        <div class="group-header" :class="{ 'group-active': active === item.name }">
          <i :class="item.icon + ' group-icon me-2'"></i>
          <span>{{ item.title }}</span>
        </div>

        <!-- Submódulos: siempre visibles -->
        <ul class="submenu list-unstyled">
          <li v-for="sub in item.submenu" :key="sub.link">
            <router-link
              :to="sub.link"
              class="submenu-item"
              :class="{ active: rutaActiva(sub.link) }"
              @click="seleccionarOpcion(item.name)"
            >
              <i :class="(sub.icono || 'bi bi-dot') + ' submenu-icon'"></i>
              <span>{{ sub.title }}</span>
            </router-link>
          </li>
        </ul>
      </li>
    </ul>
    <!-- CERRAR SESIÓN -->
    <div class="mt-auto footer-actions">
      <button type="button" class="logout-btn" @click="logout">
        <i class="bi bi-box-arrow-right me-2"></i>
        Cerrar sesión
      </button>
    </div>
  </nav>
</template>


<script>
import api from '@/services/api';
import { ui, closeSidebar } from '@/stores/ui';

export default {
  name: 'AppSidebar',

  emits: ['close-sidebar'],

  data() {
    return {
      active: '',
      isCollapsed: false,
      openSubmenu: null,

      usuario: {},
      permisos: [],

      // El menú se construye dinámicamente desde la BD (GET /api/menu) en
      // cargarMenu(). Este arreglo es solo un fallback mínimo si la API falla.
      menu: [
        {
          name: 'grp_inicio',
          title: 'Inicio',
          icon: 'bi bi-speedometer2',
          submenu: [
            { title: 'Dashboard', link: '/dashboard', modulo: 'dashboard', accion: 'listar' }
          ]
        }
      ]
    };
  },

  computed: {
    // Estado global de la barra (abrir/cerrar).
    sidebarAbierta() {
      return ui.sidebarOpen;
    },

    esAdministrador() {
      const valor = this.usuario?.es_admin;

      const nombreRol = String(
        this.usuario?.role?.nombre ||
        this.usuario?.rol?.nombre ||
        ''
      )
        .trim()
        .toLowerCase();

      return (
        valor === true ||
        valor === 1 ||
        valor === '1' ||
        valor === 'true' ||
        nombreRol === 'administrador' ||
        nombreRol === 'admin'
      );
    },

    menuFiltrado() {
      // El administrador visualiza todo.
      if (this.esAdministrador) {
        return this.menu;
      }

      return this.menu.reduce((resultado, grupo) => {
        /*
         * Validar permiso principal.
         * Actualmente se utiliza en Inventario.
         */
        if (grupo.permisoPrincipal) {
          const permitido = this.tienePermiso(
            grupo.permisoPrincipal.modulo,
            grupo.permisoPrincipal.accion
          );

          if (!permitido) {
            return resultado;
          }
        }

        // Filtrar las opciones permitidas.
        const submenuPermitido = grupo.submenu.filter((opcion) => {
          return this.tienePermiso(
            opcion.modulo,
            opcion.accion
          );
        });

        // Si no queda ninguna opción, ocultar el grupo completo.
        if (submenuPermitido.length === 0) {
          return resultado;
        }

        resultado.push({
          ...grupo,
          submenu: submenuPermitido
        });

        return resultado;
      }, []);
    }
  },

  watch: {
    '$route.path': {
      immediate: true,

      handler() {
        this.marcarMenuActivo();
      }
    }
  },

  mounted() {
    this.cargarSesion();
    this.cargarMenu();

    window.addEventListener(
      'storage',
      this.cargarSesion
    );

    window.addEventListener(
      'permisos-actualizados',
      this.cargarSesion
    );

    window.addEventListener(
      'focus',
      this.cargarSesion
    );
  },

  beforeUnmount() {
    window.removeEventListener(
      'storage',
      this.cargarSesion
    );

    window.removeEventListener(
      'permisos-actualizados',
      this.cargarSesion
    );

    window.removeEventListener(
      'focus',
      this.cargarSesion
    );
  },


  methods: {
    // Carga el árbol del menú (grupos -> submódulos) desde la BD.
    async cargarMenu() {
      try {
        const { data } = await api.get('menu');
        if (Array.isArray(data) && data.length) {
          this.menu = data;
          this.marcarMenuActivo();
        }
      } catch (error) {
        // Si la API falla, se conserva el fallback mínimo definido en data().
        console.error('No se pudo cargar el menú desde la API:', error);
      }
    },

    cargarSesion() {
      try {
        const usuarioGuardado = JSON.parse(
          localStorage.getItem('user') || '{}'
        );

        this.usuario = usuarioGuardado;

        /*
         * Primero intenta leer user.permisos.
         */
        if (Array.isArray(usuarioGuardado.permisos)) {
          this.permisos = usuarioGuardado.permisos;
        } else {
          /*
           * Como tus permisos están guardados así:
           *
           * permisos_ventas
           * permisos_compras
           * permisos_inventario
           *
           * los recorremos y formamos un arreglo.
           */
          this.permisos =
            this.obtenerPermisosLocalStorage();
        }

       
      } catch (error) {
        alert.error(
          'Error al cargar la sesión:',
          error
        );

        this.usuario = {};
        this.permisos = [];
      }
    },

    obtenerPermisosLocalStorage() {
      const permisosEncontrados = [];

      for (
        let indice = 0;
        indice < localStorage.length;
        indice += 1
      ) {
        const clave = localStorage.key(indice);

        if (
          !clave ||
          !clave.startsWith('permisos_')
        ) {
          continue;
        }

        try {
          const modulo = clave
            .replace('permisos_', '')
            .trim()
            .toLowerCase();

          const acciones = JSON.parse(
            localStorage.getItem(clave) || '{}'
          );

          permisosEncontrados.push({
            modulo,
            listar: acciones.listar ?? false,
            crear: acciones.crear ?? false,
            actualizar: acciones.actualizar ?? false,
            eliminar: acciones.eliminar ?? false
          });
        } catch (error) {
          alert.error(
            `No se pudo leer ${clave}:`,
            error
          );
        }
      }

      return permisosEncontrados;
    },

    tienePermiso(modulo, accion = 'listar') {
      if (this.esAdministrador) {
        return true;
      }

      const moduloBuscado = String(modulo)
        .trim()
        .toLowerCase();

      // El Inicio/Dashboard es el módulo por defecto: siempre visible,
      // aunque el usuario no tenga rol ni permisos asignados.
      if (moduloBuscado === 'dashboard') {
        return true;
      }

      const permiso = this.permisos.find((item) => {
        return (
          String(item.modulo)
            .trim()
            .toLowerCase() === moduloBuscado
        );
      });

      if (!permiso) {
        return false;
      }

      const valor = permiso[accion];

      return (
        valor === true ||
        valor === 1 ||
        valor === '1' ||
        valor === 'true'
      );
    },

    toggleSubmenu(nombreGrupo) {
      this.openSubmenu =
        this.openSubmenu === nombreGrupo
          ? null
          : nombreGrupo;

      this.active = nombreGrupo;
    },

    seleccionarOpcion(nombreGrupo) {
      this.active = nombreGrupo;
      // Al navegar a un módulo, se cierra la barra (drawer) de forma fluida.
      closeSidebar();
      this.$emit('close-sidebar');
    },

    cerrarBarra() {
      closeSidebar();
    },

    marcarMenuActivo() {
      const rutaActual = String(
        this.$route.path || ''
      )
        .trim()
        .toLowerCase();

      const grupoEncontrado =
        this.menuFiltrado.find((grupo) => {
          return grupo.submenu.some((opcion) => {
            return this.rutaCoincide(
              opcion.link,
              rutaActual
            );
          });
        });

      if (grupoEncontrado) {
        this.active = grupoEncontrado.name;
        this.openSubmenu = grupoEncontrado.name;
      } else {
        this.active = '';
        this.openSubmenu = null;
      }
    },

    rutaCoincide(link, rutaActual) {
      const rutaMenu = String(link)
        .trim()
        .toLowerCase();

      const ruta = String(rutaActual)
        .trim()
        .toLowerCase();

      return (
        ruta === rutaMenu ||
        ruta.startsWith(`${rutaMenu}/`)
      );
    },

    rutaActiva(link) {
      return (
        String(this.$route.path)
          .trim()
          .toLowerCase() ===
        String(link)
          .trim()
          .toLowerCase()
      );
    },

    logout() {
      const confirmar = window.confirm(
        '¿Seguro que deseas cerrar sesión?'
      );

      if (!confirmar) {
        return;
      }

      localStorage.clear();

      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 250px;
  height: 100vh;
  background: #ffffff;
  border-right: 1px solid #e8edf3;
  z-index: 1030;
  overflow-y: auto;
  transition: transform .3s ease;
}

/* Abrir/cerrar fluido (drawer). Gana sobre .d-none de Bootstrap. */
.sidebar.sb-open {
  display: flex !important;
  transform: translateX(0);
}

.sidebar.sb-closed {
  transform: translateX(-100%);
}

/* Fondo oscuro (solo móvil) al abrir la barra */
.sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, .45);
  z-index: 1025;
}

.sidebar::-webkit-scrollbar {
  width: 6px;
}

.sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar::-webkit-scrollbar-thumb {
  background: #cfd8e3;
  border-radius: 20px;
}

.sidebar::-webkit-scrollbar-thumb:hover {
  background: #aeb8c4;
}

/* HEADER */

.sidebar-header {
  padding-bottom: 18px;
  margin-bottom: 8px;
  border-bottom: 1px solid #eef2f6;
}

.brand-title {
  display: flex;
  align-items: center;
  text-decoration: none;
  font-size: 22px;
  font-weight: 800;
  color: #0B5EA8;
  letter-spacing: -.3px;
}

.brand-title i {
  color: #F58220;
  font-size: 22px;
}

.brand-title:hover {
  color: #084b85;
}

.btn-close-custom {
  border: none;
  background: none;
  color: #667085;
  font-size: 24px;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  transition: .2s;
}

.btn-close-custom:hover {
  background: #f5f7fa;
}

/* MENU */

.menu {
  margin-top: 12px;
}

.menu li {
  margin-bottom: 5px;
}

/* GRUPO (sección) — encabezado siempre visible, no colapsa */
.menu-group {
  margin-bottom: 10px;
}

.group-header {
  display: flex;
  align-items: center;

  padding: 10px 12px 6px;

  color: #475467;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .01em;

  user-select: none;
}

.group-icon {
  font-size: 17px;
  color: #0B5EA8;   /* íconito azul que diferencia al grupo */
}

/* Resalta sutilmente el grupo cuyo submódulo está activo */
.group-header.group-active {
  color: #0B5EA8;
}

/* SUBMENU — siempre visible, indentado */

.submenu {
  margin-top: 2px;
  margin-bottom: 4px;
  margin-left: 16px;
  padding-left: 10px;
  border-left: 2px solid #eef2f6;
}

.submenu-item {
  display: flex;
  align-items: center;

  padding: 9px 12px;

  border-radius: 8px;

  text-decoration: none;

  color: #667085;

  font-size: 14px;
  font-weight: 500;

  transition: .18s ease;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.submenu-icon {
  font-size: 15px;
  line-height: 1;
  width: 20px;
  text-align: center;
  margin-right: 8px;
  color: #98a2b3;   /* ícono gris del submódulo */
  flex-shrink: 0;
}

.submenu-item:hover {
  background: #f5f8fc;
  color: #0B5EA8;
}

.submenu-item:hover .submenu-icon {
  color: #0B5EA8;
}

.submenu-item.active {
  background: #eef6ff;
  color: #0B5EA8;
  font-weight: 700;
}

.submenu-item.active .submenu-icon {
  color: #0B5EA8;
}

/* FOOTER */

.footer-actions {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #eef2f6;
}

/* BOTON LOGOUT */

.logout-btn {
  width: 100%;

  border: none;

  background: #fff4ed;

  color: #d35400;

  padding: 12px;

  border-radius: 10px;

  font-size: 14px;
  font-weight: 700;

  transition: .2s ease;
}

.logout-btn:hover {
  background: #F58220;
  color: #ffffff;
}

/* ANIMACION */

.fade-enter-active,
.fade-leave-active {
  transition: all .20s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* RESPONSIVE */

@media (max-width:991px) {

  .sidebar {
    width: 280px;
    max-width: 85vw;
    box-shadow: 0 18px 45px rgba(15, 23, 42, .18);
  }

}

@media (max-width:576px) {

  .sidebar {
    width: 100%;
    max-width: 320px;
  }

  .brand-title {
    font-size: 20px;
  }

  .group-header {
    padding: 10px 12px 6px;
    font-size: 13px;
  }

  .submenu-item {
    font-size: 13.5px;
  }

}
</style>